<template>
  <v-card
    class="base-data-table-card"
    flat
  >
    <div class="base-data-table-header">
      <div class="base-data-table-title">
        <v-icon
          :icon="headerIcon"
          color="#3ACAC9"
          size="small"
        />
        {{ title }}
      </div>
      <slot name="header-actions" />
    </div>
    <div
      :class="{ 'base-data-table-scroll-wrapper': scrollable }"
      :style="scrollWrapperStyle"
    >
      <v-table
        class="base-data-table"
        :class="{ 'base-data-table--fixed': columnWidths.length > 0 }"
      >
        <colgroup v-if="columnWidths.length > 0">
          <col
            v-for="(width, columnIndex) in columnWidths"
            :key="columnIndex"
            :style="width ? { width } : null"
          >
        </colgroup>
        <thead>
          <tr>
            <slot name="header-cells" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in items"
            :key="getRowKey(row, index)"
          >
            <slot
              name="cells"
              :row="row"
              :index="index"
            />
          </tr>
        </tbody>
      </v-table>
      <div
        v-if="items.length === 0"
        class="base-data-table-empty"
      >
        まだデータはありません
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "FaceSheetBaseDataTable",
  props: {
    items: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    headerIcon: {
      type: String,
      default: "mdi-table"
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    scrollHeight: {
      type: String,
      default: "260px"
    },
    // 各列の固定幅（例: ["120px", "200px", ...]）。指定するとテーブルは
    // table-layout: fixed で描画され、長文はセル内で折り返す。空文字の要素は
    // 幅指定なし（残り幅を分配）。
    columnWidths: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    scrollWrapperStyle() {
      return this.scrollable ? { maxHeight: this.scrollHeight } : null;
    }
  },
  methods: {
    getRowKey(row, index) {
      return row?.id ?? index;
    }
  }
};
</script>

<style lang="scss" scoped>
.base-data-table-card {
  width: 100%;
  padding: 16px 12px 0;
  background-color: #fff;
}

.base-data-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.base-data-table-title {
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: bold;
}

.base-data-table :deep(thead th) {
  background-color: #dbf4f3 !important;
  font-weight: bold !important;
  white-space: nowrap;
}

.base-data-table :deep(td),
.base-data-table :deep(th) {
  font-size: 0.875rem;
}

/* 列幅を colgroup で固定した場合、長文はセル内で折り返す。
   v-table（root）→ wrapper → table まで横幅を 100% に揃えないと、
   table-layout: fixed 下で伸縮列（幅未指定）が最小幅に潰れてしまう。 */
.base-data-table--fixed {
  width: 100%;
}

.base-data-table--fixed :deep(.v-table__wrapper) {
  width: 100%;
}

.base-data-table--fixed :deep(table) {
  table-layout: fixed;
  width: 100% !important;
}

.base-data-table--fixed :deep(td) {
  overflow-wrap: anywhere;
  white-space: pre-line;
  word-break: break-word;
}

/* アクション列（ボタン）は折り返さない。伸縮列の word-break がボタンラベルに
   効いて崩れるのを打ち消す。 */
.base-data-table--fixed :deep(.base-data-table-action-cell) {
  overflow-wrap: normal;
  white-space: nowrap;
  word-break: normal;
}

.base-data-table--fixed :deep(thead th) {
  overflow-wrap: anywhere;
  white-space: normal;
  word-break: break-word;
}

.base-data-table :deep(.base-data-table-empty) {
  color: rgba(0, 0, 0, 0.56);
  padding: 16px;
  text-align: center;
}

.base-data-table-empty {
  color: rgba(0, 0, 0, 0.56);
  padding: 16px;
  text-align: center;
}

.base-data-table-scroll-wrapper {
  overflow-y: auto;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.base-data-table-scroll-wrapper :deep(thead th) {
  position: sticky;
  top: 0;
  z-index: 1;
}

:global(.base-data-table-action-cell) {
  text-align: right;
  white-space: nowrap;
}

:global(.base-data-table-link) {
  color: #3ACAC9;
  text-decoration: underline;
}

:global(.base-data-table-two-line-clamp) {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
  white-space: pre-line;
  word-break: break-word;
}
</style>
